import { formatCurrency } from "@/components/operations/operations-shared";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import type { MercuryBalance } from "@/core/ports/services/functions-service";
import {
  useAirwallexBalances,
  useAirwallexBalanceSnapshots,
  useMercuryBalances,
  useMercuryBalanceSnapshots,
} from "@/hooks";
import {
  exportBalancesToCsv,
  type BalanceExportRow,
} from "@/utils/csv-export";
import { Download, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const HISTORY_DAYS = 90;

type RangeFilter = "90d" | "mtd" | "ytd" | "all";

const RANGE_OPTIONS: { label: string; value: RangeFilter }[] = [
  { label: "Last 90 days", value: "90d" },
  { label: "Month to date", value: "mtd" },
  { label: "Year to date", value: "ytd" },
  { label: "All time", value: "all" },
];

/** Snapshot history start date for the selected range. */
function rangeStart(range: RangeFilter): Date {
  const today = new Date();
  if (range === "mtd") return new Date(today.getFullYear(), today.getMonth(), 1);
  if (range === "ytd") return new Date(today.getFullYear(), 0, 1);
  if (range === "all") return new Date(2020, 0, 1);
  const d = new Date(today);
  d.setDate(d.getDate() - HISTORY_DAYS);
  return d;
}

const LINE_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#16a34a",
  "#ea580c",
  "#0891b2",
  "#db2777",
];

const compactCurrency = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

function toDateKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// A daily balance snapshot; both banks share this shape.
interface BalanceSnapshotLike {
  accountId: string;
  accountName?: string;
  date: string;
  availableBalance: number;
}

interface BankBalancesProps {
  bankName: string;
  snapshotScript: string;
  balancesQuery: UseQueryResult<MercuryBalance[]>;
  snapshotsQuery: UseQueryResult<BalanceSnapshotLike[]>;
}

/** Live balances + snapshot history for one bank. Rendered once per bank. */
function BankBalances({
  bankName,
  snapshotScript,
  balancesQuery,
  snapshotsQuery,
}: BankBalancesProps) {
  const balances = useMemo(
    () => balancesQuery.data ?? [],
    [balancesQuery.data],
  );

  const totals = useMemo(
    () => ({
      available: balances.reduce((sum, b) => sum + b.availableBalance, 0),
      current: balances.reduce((sum, b) => sum + b.currentBalance, 0),
    }),
    [balances],
  );

  const { accounts, chartData } = useMemo(() => {
    const snaps = snapshotsQuery.data ?? [];
    const acctLabel = new Map<string, string>();
    const byDate = new Map<string, Record<string, number | string>>();
    for (const snap of snaps) {
      acctLabel.set(snap.accountId, snap.accountName || snap.accountId);
      const row = byDate.get(snap.date) ?? { date: snap.date };
      row[snap.accountId] = snap.availableBalance;
      byDate.set(snap.date, row);
    }
    return {
      accounts: [...acctLabel.entries()].map(([id, label]) => ({ id, label })),
      chartData: [...byDate.values()].sort((a, b) =>
        String(a.date).localeCompare(String(b.date)),
      ),
    };
  }, [snapshotsQuery.data]);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle>{bankName} Balances</CardTitle>
            <CardDescription>
              Live balances of your {bankName} accounts, with a daily snapshot
              history. "Available" is spendable now; "current" includes pending
              transactions.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            loading={balancesQuery.isFetching}
            loadingText="Refreshing…"
            onClick={() => balancesQuery.refetch()}
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {balancesQuery.isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Loading balances…
            </p>
          ) : balancesQuery.isError ? (
            <p className="py-8 text-center text-sm text-destructive">
              Could not load balances. Check the {bankName} API credentials.
            </p>
          ) : balances.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No {bankName} accounts found.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-xs font-medium text-muted-foreground">
                  Total available
                </p>
                <p className="mt-1 font-mono text-2xl font-semibold">
                  {formatCurrency({ value: totals.available })}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Current: {formatCurrency({ value: totals.current })} ·{" "}
                  {balances.length} account
                  {balances.length === 1 ? "" : "s"}
                </p>
              </div>

              {balances.map((b) => (
                <div key={b.accountId} className="rounded-lg border p-4">
                  <p className="truncate text-sm font-medium">
                    {b.accountName ?? b.accountId}
                  </p>
                  {b.kind && (
                    <p className="text-xs capitalize text-muted-foreground">
                      {b.kind}
                    </p>
                  )}
                  <p className="mt-1 font-mono text-2xl font-semibold">
                    {formatCurrency({ value: b.availableBalance })}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Current: {formatCurrency({ value: b.currentBalance })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{bankName} balance history</CardTitle>
          <CardDescription>
            Available balance per account over the selected range (daily
            snapshots).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {snapshotsQuery.isLoading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              Loading history…
            </p>
          ) : chartData.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No balance history yet. The first daily snapshot will appear after
              the scheduled sync runs (or run <code>{snapshotScript}</code>).
            </p>
          ) : (
            <ChartContainer
              className="aspect-auto h-[320px] w-full"
              config={Object.fromEntries(
                accounts.map((a, i) => [
                  a.id,
                  { label: a.label, color: LINE_COLORS[i % LINE_COLORS.length] },
                ]),
              )}
            >
              <LineChart data={chartData} margin={{ left: 12, right: 12 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  minTickGap={32}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={64}
                  tickFormatter={(value: number) =>
                    compactCurrency.format(value)
                  }
                />
                <Tooltip
                  formatter={(value, name) => [
                    formatCurrency({ value: Number(value) }),
                    accounts.find((a) => a.id === name)?.label ?? String(name),
                  ]}
                />
                <Legend
                  formatter={(value) =>
                    accounts.find((a) => a.id === value)?.label ?? String(value)
                  }
                />
                {accounts.map((a, i) => (
                  <Line
                    key={a.id}
                    type="monotone"
                    dataKey={a.id}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>
    </>
  );
}

export default function BalancesPage() {
  const [rangeFilter, setRangeFilter] = useState<RangeFilter>("90d");

  const range = useMemo(
    () => ({
      startDate: toDateKey(rangeStart(rangeFilter)),
      endDate: toDateKey(new Date()),
    }),
    [rangeFilter],
  );

  const mercuryBalancesQuery = useMercuryBalances();
  const mercurySnapshotsQuery = useMercuryBalanceSnapshots(range);
  const airwallexBalancesQuery = useAirwallexBalances();
  const airwallexSnapshotsQuery = useAirwallexBalanceSnapshots(range);

  function handleExportCsv() {
    const rows: BalanceExportRow[] = [
      ...(mercuryBalancesQuery.data ?? []).map((b) => ({
        bank: "Mercury",
        account: b.accountName ?? b.accountId,
        currency: b.currency,
        available: b.availableBalance,
        current: b.currentBalance,
      })),
      ...(airwallexBalancesQuery.data ?? []).map((b) => ({
        bank: "Airwallex",
        account: b.accountName ?? b.accountId,
        currency: b.currency,
        available: b.availableBalance,
        current: b.currentBalance,
      })),
    ];
    exportBalancesToCsv(rows);
  }

  const canExport =
    (mercuryBalancesQuery.data?.length ?? 0) +
      (airwallexBalancesQuery.data?.length ?? 0) >
    0;

  return (
    <div className="container mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      {/* Range + export toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            History range
          </span>
          {RANGE_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              variant={rangeFilter === opt.value ? "default" : "outline"}
              size="sm"
              onClick={() => setRangeFilter(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!canExport}
          onClick={handleExportCsv}
        >
          <Download className="h-4 w-4" />
          Export balances CSV
        </Button>
      </div>

      <BankBalances
        bankName="Mercury"
        snapshotScript="npm run snapshot-mercury-balances"
        balancesQuery={mercuryBalancesQuery}
        snapshotsQuery={mercurySnapshotsQuery}
      />
      <BankBalances
        bankName="Airwallex"
        snapshotScript="npm run snapshot-airwallex-balances"
        balancesQuery={airwallexBalancesQuery}
        snapshotsQuery={airwallexSnapshotsQuery}
      />
    </div>
  );
}
